import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
} from 'firebase/messaging';

import { app } from './firebaseApp';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const SW_PATH = `${process.env.PUBLIC_URL || ''}/firebase-messaging-sw.js`;

let messagingInstance: Messaging | null = null;
let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

async function ensureMessagingServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    return null;
  }
  if (serviceWorkerRegistration) {
    return serviceWorkerRegistration;
  }
  try {
    serviceWorkerRegistration = await navigator.serviceWorker.register(SW_PATH);
    return serviceWorkerRegistration;
  } catch (error) {
    console.warn('FCM service worker registration failed:', error);
    return null;
  }
}

async function getMessagingInstance(): Promise<Messaging | null> {
  if (!(await isSupported())) {
    return null;
  }
  if (!messagingInstance) {
    messagingInstance = getMessaging(app);
  }
  return messagingInstance;
}

export async function getFcmToken(): Promise<string | null> {
  if (!VAPID_KEY) {
    console.warn(
      'REACT_APP_FIREBASE_VAPID_KEY is not set; skipping FCM token fetch',
    );
    return null;
  }
  const messaging = await getMessagingInstance();
  const registration = await ensureMessagingServiceWorker();
  if (!messaging || !registration) {
    return null;
  }
  try {
    return await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
  } catch (error) {
    console.warn('FCM getToken failed:', error);
    return null;
  }
}

export function initForegroundMessaging(
    onPayload: (payload: unknown) => void,
): () => void {
  let cancelled = false;
  (async () => {
    const messaging = await getMessagingInstance();
    if (!messaging || cancelled) {
      return;
    }
    onMessage(messaging, (payload) => {
      onPayload(payload);
    });
  })();
  return () => {
    cancelled = true;
  };
}
